import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Tag,
  Text,
  VStack,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import type { SavedSelection } from '../savedSelectionsStorage';

export type RenameSelectionResult = 'ok' | 'duplicate' | 'storage-failed';

export interface ManageSelectionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  savedSelections: SavedSelection[];
  onRename: (id: string, name: string) => RenameSelectionResult;
  onDelete: (id: string) => boolean;
}

const CONFIRM_REVERT_MS = 3000;

const ManageSelectionsModal: React.FC<ManageSelectionsModalProps> = ({
  isOpen,
  onClose,
  savedSelections,
  onRename,
  onDelete,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const confirmTimerRef = useRef<number | null>(null);

  const clearConfirmTimer = () => {
    if (confirmTimerRef.current !== null) {
      window.clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
    }
  };

  // Reset all interaction state when the modal closes.
  useEffect(() => {
    if (!isOpen) {
      setEditingId(null);
      setDraft('');
      setRenameError(null);
      setPendingDeleteId(null);
      clearConfirmTimer();
    }
  }, [isOpen]);

  // Belt-and-braces unmount cleanup for the confirm-revert timer.
  useEffect(() => clearConfirmTimer, []);

  const startEdit = (s: SavedSelection) => {
    clearConfirmTimer();
    setPendingDeleteId(null);
    setEditingId(s.id);
    setDraft(s.name);
    setRenameError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft('');
    setRenameError(null);
  };

  const commitEdit = (originalName: string) => {
    if (editingId === null) return;
    const trimmed = draft.trim();
    if (!trimmed) {
      cancelEdit();
      return;
    }
    if (trimmed === originalName.trim()) {
      cancelEdit();
      return;
    }
    const result = onRename(editingId, trimmed);
    if (result === 'ok') {
      cancelEdit();
    } else if (result === 'duplicate') {
      setRenameError('Name already used.');
    } else {
      // 'storage-failed': parent already surfaced a toast; close the editor
      // so the user doesn't see a misleading inline error on top of the toast.
      cancelEdit();
    }
  };

  const requestDelete = (id: string) => {
    clearConfirmTimer();
    setPendingDeleteId(id);
    confirmTimerRef.current = window.setTimeout(() => {
      confirmTimerRef.current = null;
      setPendingDeleteId((curr) => (curr === id ? null : curr));
    }, CONFIRM_REVERT_MS);
  };

  const confirmDelete = (id: string) => {
    clearConfirmTimer();
    setPendingDeleteId(null);
    onDelete(id);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent mx="1rem">
        <ModalHeader>Manage saved selections</ModalHeader>
        <ModalBody>
          {savedSelections.length === 0 ? (
            <Text color="gray.500" py={4} textAlign="center">
              Nothing saved yet.
            </Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {savedSelections.map((s) => {
                const isEditing = editingId === s.id;
                const isPending = pendingDeleteId === s.id;
                return (
                  <VStack key={s.id} align="stretch" spacing={1}>
                    <HStack spacing={2}>
                      {isEditing ? (
                        <Input
                          flex="1"
                          autoFocus
                          size="sm"
                          value={draft}
                          maxLength={60}
                          onChange={(e) => {
                            setDraft(e.target.value);
                            if (renameError) setRenameError(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              commitEdit(s.name);
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelEdit();
                            }
                          }}
                          onBlur={() => commitEdit(s.name)}
                        />
                      ) : (
                        <Text flex="1" isTruncated>
                          {s.name}
                        </Text>
                      )}
                      <Tag size="sm" variant="subtle">
                        {s.companyIds.length.toLocaleString()}
                      </Tag>
                      <Spacer />
                      {!isEditing && (
                        <IconButton
                          aria-label={`Rename ${s.name}`}
                          icon={<EditIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={() => startEdit(s)}
                        />
                      )}
                      {isPending ? (
                        <Button
                          size="xs"
                          colorScheme="red"
                          onClick={() => confirmDelete(s.id)}
                        >
                          Confirm?
                        </Button>
                      ) : (
                        <IconButton
                          aria-label={`Delete ${s.name}`}
                          icon={<DeleteIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={() => requestDelete(s.id)}
                        />
                      )}
                    </HStack>
                    {isEditing && renameError && (
                      <Text fontSize="xs" color="red.500" pl={1}>
                        {renameError}
                      </Text>
                    )}
                  </VStack>
                );
              })}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack w="100%" spacing={2}>
            <Spacer />
            <Button onClick={onClose}>Close</Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ManageSelectionsModal;
